<template>
	<div v-if="enabled" class="ai_chat">
		<transition name="ai_chat_pop">
			<div v-if="open" class="ai_chat_panel" :class="{ 'is-wide': wide }"
				role="dialog" aria-label="Asistente Phi">
				<div class="ai_chat_header">
					<span class="ai_chat_avatar" aria-hidden="true">φ</span>
					<span class="ai_chat_title">Pregunta sobre tus finanzas a Phi</span>
					<!-- Las tablas de Phi (comparaciones por año) no entran en el
					ancho base: el panel se agranda a pedido. -->
					<button class="ai_chat_expand" :aria-pressed="String(wide)"
						:aria-label="wide ? 'Restaurar el tamaño del chat' : 'Agrandar el chat'"
						@click="wide = !wide">
						<svg width="12" height="12" viewBox="0 0 12 12" fill="none"
							aria-hidden="true">
							<path d="M7 1h4v4M5 11H1V7M11 1L7 5M1 11l4-4"
								stroke="currentColor" stroke-width="1.6"
								stroke-linecap="round" stroke-linejoin="round" />
						</svg>
					</button>
					<button class="ai_chat_close" aria-label="Cerrar chat"
						@click="open = false">
						<svg width="12" height="12" viewBox="0 0 12 12" fill="none"
							aria-hidden="true">
							<path d="M1 1l10 10M11 1L1 11" stroke="currentColor"
								stroke-width="1.6" stroke-linecap="round" />
						</svg>
					</button>
				</div>

				<div ref="messagesBox" class="ai_chat_messages">
					<div v-if="!messages.length" class="ai_chat_welcome">
						<span class="ai_chat_welcome-mark" aria-hidden="true">φ</span>
						<p class="ai_chat_empty">
							Pregúntame lo que necesitas saber de tus clasificaciones, KPIs, períodos de tiempo.
						</p>
						<div class="ai_chat_suggestions">
							<button v-for="suggestion in suggestions" :key="suggestion"
								class="ai_chat_suggestion" :disabled="streaming"
								@click="sendSuggestion(suggestion)">
								{{ suggestion }}
							</button>
						</div>
					</div>

					<div v-for="(msg, index) in messages" :key="index"
						:class="[
							msg.role === 'user' ? 'ai_msg_user' : 'ai_msg_assistant',
							{ 'is-streaming': isStreamingBubble(msg, index) },
						]">
						<!-- Sólo las respuestas de Phi se interpretan como
						markdown; lo que escribe el usuario va literal. -->
						<AiMarkdown v-if="msg.role === 'assistant'"
							:text="msg.content" />
						<template v-else>{{ msg.content }}</template>
					</div>

					<div v-if="toolLabel" class="ai_chat_tool">
						<span class="ai_chat_tool-spinner" aria-hidden="true"></span>
						{{ toolLabel }}
					</div>

					<div v-if="error" class="ai_chat_error">
						{{ error }}
						<button v-if="pending && !quotaHit" class="ai_chat_retry" @click="retry">
							Reintentar
						</button>
					</div>
				</div>

				<form class="ai_chat_form" @submit.prevent="send">
					<textarea ref="input" v-model="input" class="ai_chat_input"
						rows="1" :disabled="streaming" maxlength="4000"
						enterkeyhint="send" aria-label="Tu pregunta para Phi"
						placeholder="Escribí tu pregunta… (Shift+Enter = nueva línea)"
						@input="resizeInput"
						@keydown.enter.exact.prevent="send"></textarea>
					<button class="ai_chat_send" :disabled="streaming || !input.trim()"
						aria-label="Enviar">
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none"
							aria-hidden="true">
							<path d="M8 13V3M3.5 7.5L8 3l4.5 4.5" stroke="currentColor"
								stroke-width="1.8" stroke-linecap="round"
								stroke-linejoin="round" />
						</svg>
					</button>
				</form>
			</div>
		</transition>

		<button class="ai_chat_fab" :class="{ 'is-open': open }"
			:aria-label="open ? 'Cerrar asistente' : 'Abrir asistente Phi'"
			@click="open = !open">
			<span aria-hidden="true">{{ open ? "✕" : "φ" }}</span>
		</button>
	</div>
</template>

<script>
// Widget del chatbot Phi (Fase 4 — docs/PLAN_MCP_SERVER.md). Estado LOCAL
// del componente (sin store Vuex, decisión del plan): el historial vive en
// memoria y viaja completo a /chat (el sidecar es stateless). Sin
// VUE_APP_AI_URL, o sin el flag has_ai_chat del usuario (lo prende el
// analista, fase 2c), el widget no se monta — el gate visual es cosmético:
// la barrera real es el 403 pre-LLM de /chat en el sidecar.
//
// Identidad visual: tokens --ai-chat-* (_report_themes.scss), alineados al
// teal Phi del flujo de conexión MCP y del auth theme.
import AiMarkdown from "@/components/aiChat/AiMarkdown.vue";
import config from "@/config";
import { getSession } from "@/utils/auth";
import { readStringFlag } from "@/utils/localFlag";
import { streamChat } from "@/utils/aiChatStream";

// Labels mostrables mientras corre una tool (display-only).
const TOOL_LABELS = Object.freeze({
	list_companies: "revisando tus empresas",
	get_current_company: "verificando la empresa activa",
	get_data_availability: "revisando qué datos hay",
	get_income_statement: "consultando el estado de resultados",
	get_kpis: "consultando KPIs",
	get_summary_cards: "consultando el resumen",
	get_yoy_comparison: "comparando años",
	get_top_counterparties: "consultando el ranking",
	get_breakdown: "consultando la composición",
	get_ratio_to_sales: "calculando el peso sobre las ventas",
	get_accrued_counterparties: "consultando devengados",
	get_unclassified_counterparts: "revisando lo que falta clasificar",
	get_cash_position: "consultando la caja",
});

// Techo de alto del textarea (~5 líneas de 13px/1.55 + padding): más que eso
// scrollea adentro en vez de comerse el hilo de mensajes.
const MAX_INPUT_HEIGHT = 120;

// Aperturas sugeridas en el estado vacío — preguntas del banco validado que
// una sola tool responde bien (arranque con éxito garantizado).
const SUGGESTIONS = Object.freeze([
	"¿Cómo van mis ventas este año?",
	"¿Cuál es mi margen bruto?",
	"¿Quién es mi cliente más importante?",
]);

export default {
	name: "AiChatWidget",
	components: { AiMarkdown },
	data() {
		return {
			open: false,
			// Panel agrandado (para leer tablas anchas). No persiste: cada
			// apertura arranca en el tamaño base, igual que el historial.
			wide: false,
			input: "",
			messages: [],
			streaming: false,
			toolLabel: "",
			error: "",
			// Cupo agotado (429): estado propio, sin botón de reintento —
			// reintentar ahora no puede funcionar, el cupo renueva solo.
			quotaHit: false,
			// Último envío, para reintentar sin duplicar el turno del usuario.
			pending: null,
			suggestions: SUGGESTIONS,
		};
	},
	watch: {
		// Reabrir el panel con historial: el hilo arranca en el fondo (los
		// últimos mensajes), no en el primero.
		open(isOpen) {
			if (isOpen && this.messages.length) {
				this.scrollDown();
			}
		},
	},
	computed: {
		enabled() {
			const session = getSession();
			// session.user es el payload del login/refresh: el flag se
			// refresca en el próximo login o refresh del token, no en vivo.
			return Boolean(
				config.aiAPI && session?.token && session?.user?.has_ai_chat,
			);
		},
	},
	methods: {
		// Enter envía y Shift+Enter salta de línea: el `.exact` del keydown
		// deja pasar cualquier combinación con modificador al comportamiento
		// nativo del textarea, así que el salto no necesita handler propio.
		resizeInput() {
			const el = this.$refs.input;
			if (!el) {
				return;
			}
			// Medir desde cero: sin el reset, scrollHeight no baja al borrar
			// texto y el campo queda estirado.
			el.style.height = "auto";
			const next = Math.min(el.scrollHeight, MAX_INPUT_HEIGHT);
			// Sin medición disponible se cae a la altura del CSS.
			el.style.height = next ? `${next}px` : "";
		},
		resetInputHeight() {
			if (this.$refs.input) {
				this.$refs.input.style.height = "";
			}
		},
		isStreamingBubble(msg, index) {
			return (
				this.streaming &&
				msg.role === "assistant" &&
				index === this.messages.length - 1
			);
		},
		sendSuggestion(suggestion) {
			this.input = suggestion;
			this.send();
		},
		async send() {
			const text = this.input.trim();
			if (!text || this.streaming) {
				return;
			}
			const history = this.messages
				.filter((msg) => msg.content)
				.map(({ role, content }) => ({ role, content }));
			this.messages.push({ role: "user", content: text });
			this.pending = { text, history };
			this.input = "";
			this.resetInputHeight();
			// El turno del usuario debe quedar visible YA: hasta el primer
			// delta (que puede tardar segundos si corre una tool) nadie más
			// scrollea y el mensaje quedaba bajo el fold.
			this.scrollDown();
			await this.stream();
		},
		async retry() {
			if (!this.pending || this.streaming) {
				return;
			}
			await this.stream();
		},
		async stream() {
			const { text, history } = this.pending;
			this.error = "";
			this.quotaHit = false;
			this.streaming = true;
			this.messages.push({ role: "assistant", content: "" });
			const assistant = this.messages[this.messages.length - 1];
			let failed = false;
			await streamChat({
				url: config.aiAPI,
				token: getSession()?.token,
				activeCompany: readStringFlag("activeCompanyId"),
				message: text,
				history,
				onDelta: (chunk) => {
					assistant.content += chunk;
					this.toolLabel = "";
					this.scrollDown();
				},
				onTool: (name) => {
					this.toolLabel = `${TOOL_LABELS[name] || "consultando datos"}…`;
					this.scrollDown();
				},
				onDone: () => {
					this.pending = null;
				},
				onError: (message, meta) => {
					failed = true;
					this.error = message;
					this.quotaHit = Boolean(meta && meta.quota);
				},
			});
			if (failed && !assistant.content) {
				// Burbuja vacía fuera; el turno del usuario queda para el retry.
				this.messages.pop();
			}
			this.streaming = false;
			this.toolLabel = "";
			this.scrollDown();
		},
		scrollDown() {
			this.$nextTick(() => {
				const box = this.$refs.messagesBox;
				if (box) {
					box.scrollTop = box.scrollHeight;
				}
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.ai_chat {
	position: fixed;
	right: 24px;
	bottom: 24px;
	z-index: 1000;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 14px;
	font-family: "Open Sans", sans-serif;
}

// ── FAB ─────────────────────────────────────────────────────────────────
.ai_chat_fab {
	width: 52px;
	height: 52px;
	border: none;
	border-radius: 50%;
	background: var(--ai-chat-accent);
	color: var(--ai-chat-accent-text);
	font-size: 24px;
	font-weight: 700;
	line-height: 1;
	cursor: pointer;
	box-shadow:
		0 2px 6px var(--ai-chat-shadow),
		0 10px 24px -8px var(--ai-chat-shadow);
	transition: background-color 0.15s ease-out, transform 0.15s ease-out,
		box-shadow 0.15s ease-out;

	&:hover {
		background: var(--ai-chat-accent-hover);
		transform: translateY(-1px);
		box-shadow:
			0 4px 10px var(--ai-chat-shadow),
			0 14px 28px -8px var(--ai-chat-shadow);
	}

	&:active {
		transform: translateY(0) scale(0.96);
	}

	&:focus-visible {
		outline: none;
		box-shadow: 0 0 0 3px var(--ai-chat-ring), 0 2px 6px var(--ai-chat-shadow);
	}

	&.is-open {
		font-size: 18px;
		font-weight: 400;
	}
}

// ── Panel ───────────────────────────────────────────────────────────────
.ai_chat_panel {
	width: 420px;
	max-width: calc(100vw - 48px);
	height: min(560px, calc(100vh - 130px));
	transition: width 0.18s ease-out, height 0.18s ease-out;
	display: flex;
	flex-direction: column;
	background: var(--ai-chat-bg);
	border: 1px solid var(--ai-chat-border);
	border-radius: 16px;
	box-shadow:
		0 1px 2px rgba(32, 49, 65, 0.05),
		0 18px 44px -14px var(--ai-chat-shadow);
	overflow: hidden;
	transform-origin: bottom right;

	// Modo agrandado: espacio para una tabla de varias columnas sin scroll.
	&.is-wide {
		width: min(720px, calc(100vw - 48px));
		height: min(78vh, calc(100vh - 130px));
	}
}

.ai_chat_pop-enter-active,
.ai_chat_pop-leave-active {
	transition: opacity 0.18s ease-out, transform 0.18s ease-out;
}

.ai_chat_pop-enter,
.ai_chat_pop-leave-to {
	opacity: 0;
	transform: translateY(8px) scale(0.97);
}

// ── Header ──────────────────────────────────────────────────────────────
.ai_chat_header {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 12px 14px;
	background: var(--ai-chat-bg);
	border-bottom: 1px solid var(--ai-chat-border);
}

.ai_chat_avatar {
	flex: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	border-radius: 9px;
	background: var(--ai-chat-accent);
	color: var(--ai-chat-accent-text);
	font-size: 16px;
	font-weight: 700;
}

.ai_chat_title {
	flex: 1;
	min-width: 0;
	color: var(--ai-chat-ink);
	font-size: 13.5px;
	font-weight: 600;
	line-height: 1.3;
}

.ai_chat_close,
.ai_chat_expand {
	flex: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border: none;
	border-radius: 8px;
	background: transparent;
	color: var(--ai-chat-muted);
	cursor: pointer;
	transition: background-color 0.15s ease-out, color 0.15s ease-out;

	&:hover {
		background: var(--ai-chat-surface);
		color: var(--ai-chat-ink);
	}

	&:focus-visible {
		outline: none;
		box-shadow: 0 0 0 3px var(--ai-chat-ring);
	}
}

// ── Hilo de mensajes ────────────────────────────────────────────────────
.ai_chat_messages {
	flex: 1;
	overflow-y: auto;
	padding: 14px;
	display: flex;
	flex-direction: column;
	gap: 10px;
	background: var(--ai-chat-surface);
	scrollbar-width: thin;
	scrollbar-color: var(--ai-chat-border) transparent;

	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-thumb {
		background: var(--ai-chat-border);
		border-radius: 3px;
	}
}

// Estado vacío: marca + copy de producto + aperturas sugeridas.
.ai_chat_welcome {
	margin: auto 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
	padding: 0 10px;
	text-align: center;
}

.ai_chat_welcome-mark {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 52px;
	height: 52px;
	border-radius: 16px;
	background: var(--ai-chat-accent-soft);
	color: var(--ai-chat-accent);
	font-size: 26px;
	font-weight: 700;
}

.ai_chat_empty {
	color: var(--ai-chat-muted);
	font-size: 13px;
	line-height: 1.5;
	margin: 0;
}

.ai_chat_suggestions {
	display: flex;
	flex-direction: column;
	gap: 6px;
	width: 100%;
}

.ai_chat_suggestion {
	padding: 8px 12px;
	border: 1px solid var(--ai-chat-border);
	border-radius: 999px;
	background: var(--ai-chat-bg);
	color: var(--ai-chat-ink);
	font-size: 12.5px;
	cursor: pointer;
	transition: border-color 0.15s ease-out, box-shadow 0.15s ease-out;

	&:hover:not(:disabled) {
		border-color: var(--ai-chat-accent);
		box-shadow: 0 0 0 3px var(--ai-chat-ring);
	}

	&:focus-visible {
		outline: none;
		border-color: var(--ai-chat-accent);
		box-shadow: 0 0 0 3px var(--ai-chat-ring);
	}

	&:disabled {
		opacity: 0.5;
		cursor: default;
	}
}

// Burbujas: usuario en teal con "cola" abajo-derecha; Phi en blanco con
// borde y cola abajo-izquierda.
.ai_msg_user,
.ai_msg_assistant {
	max-width: 85%;
	min-width: 0;
	padding: 9px 13px;
	border-radius: 14px;
	font-size: 13px;
	line-height: 1.5;
	word-break: break-word;
}

.ai_msg_user {
	// Los saltos que el usuario escribe con Shift+Enter se muestran tal cual.
	// La burbuja de Phi NO lleva pre-wrap: ahí los bloques los arma AiMarkdown.
	white-space: pre-wrap;
	align-self: flex-end;
	background: var(--ai-chat-accent);
	color: var(--ai-chat-accent-text);
	border-bottom-right-radius: 5px;
}

// Agrandado: la respuesta de Phi (donde viven las tablas) usa casi todo el
// ancho; el turno del usuario sigue acotado para que se lea el intercambio.
.ai_chat_panel.is-wide .ai_msg_assistant {
	max-width: 94%;
}

.ai_msg_assistant {
	align-self: flex-start;
	background: var(--ai-chat-assistant-bg);
	color: var(--ai-chat-assistant-text);
	border: 1px solid var(--ai-chat-border);
	border-bottom-left-radius: 5px;

	// Caret de streaming: late mientras Phi escribe.
	&.is-streaming::after {
		content: "▍";
		margin-left: 2px;
		color: var(--ai-chat-accent);
		animation: ai_chat_caret 1s steps(2) infinite;
	}
}

// Tool en ejecución: chip con spinner (reemplaza al itálico suelto).
.ai_chat_tool {
	align-self: flex-start;
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 6px 12px;
	border: 1px solid var(--ai-chat-border);
	border-radius: 999px;
	background: var(--ai-chat-bg);
	color: var(--ai-chat-muted);
	font-size: 12px;
}

.ai_chat_tool-spinner {
	flex: none;
	width: 12px;
	height: 12px;
	border: 2px solid var(--ai-chat-ring);
	border-top-color: var(--ai-chat-accent);
	border-radius: 50%;
	animation: ai_chat_spin 0.8s linear infinite;
}

// Error: caja danger-soft con el retry como botón real.
.ai_chat_error {
	align-self: stretch;
	padding: 10px 12px;
	border: 1px solid var(--ai-chat-error-ring);
	border-radius: 10px;
	background: var(--ai-chat-error-soft);
	color: var(--ai-chat-error);
	font-size: 12.5px;
	line-height: 1.45;
}

.ai_chat_retry {
	display: inline-flex;
	margin-left: 6px;
	padding: 2px 10px;
	border: 1px solid currentColor;
	border-radius: 999px;
	background: transparent;
	color: inherit;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	transition: background-color 0.15s ease-out;

	&:hover {
		background: rgba(217, 45, 32, 0.08);
	}
}

// ── Input ───────────────────────────────────────────────────────────────
.ai_chat_form {
	display: flex;
	// El textarea crece hacia arriba: el botón de enviar queda anclado abajo.
	align-items: flex-end;
	gap: 8px;
	padding: 10px 12px;
	background: var(--ai-chat-bg);
	border-top: 1px solid var(--ai-chat-border);
}

.ai_chat_input {
	flex: 1;
	min-width: 0;
	// Multilínea (Shift+Enter): arranca en 40px —idéntico al pill de antes—
	// y crece hasta el techo de MAX_INPUT_HEIGHT, después scrollea adentro.
	min-height: 40px;
	max-height: 120px;
	padding: 9px 16px;
	border: 1px solid var(--ai-chat-border);
	// 20px = la mitad del alto de una línea: se ve pill en una línea y
	// redondeado parejo cuando crece (999px deformaría el bloque alto).
	border-radius: 20px;
	background: var(--ai-chat-surface);
	color: var(--ai-chat-ink);
	font-family: inherit;
	font-size: 13px;
	line-height: 1.55;
	resize: none;
	overflow-y: auto;
	scrollbar-width: thin;
	transition: border-color 0.15s ease-out, box-shadow 0.15s ease-out;

	&::placeholder {
		color: var(--ai-chat-muted);
	}

	&:focus {
		outline: none;
		border-color: var(--ai-chat-accent);
		box-shadow: 0 0 0 3px var(--ai-chat-ring);
	}

	&:disabled {
		opacity: 0.7;
	}
}

.ai_chat_send {
	flex: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border: none;
	border-radius: 50%;
	background: var(--ai-chat-accent);
	color: var(--ai-chat-accent-text);
	cursor: pointer;
	transition: background-color 0.15s ease-out, transform 0.15s ease-out;

	&:hover:not(:disabled) {
		background: var(--ai-chat-accent-hover);
	}

	&:active:not(:disabled) {
		transform: scale(0.94);
	}

	&:focus-visible {
		outline: none;
		box-shadow: 0 0 0 3px var(--ai-chat-ring);
	}

	&:disabled {
		background: var(--ai-chat-border);
		color: var(--ai-chat-muted);
		cursor: default;
	}
}

// ── Mobile: bottom sheet ────────────────────────────────────────────────
@media (max-width: 480px) {
	.ai_chat {
		right: 12px;
		bottom: 12px;
	}

	.ai_chat_panel,
	.ai_chat_panel.is-wide {
		position: fixed;
		left: 12px;
		right: 12px;
		bottom: 76px;
		width: auto;
		max-width: none;
		height: min(70vh, 560px);
	}

	// En bottom-sheet el panel ya ocupa todo el ancho: agrandar no aplica.
	.ai_chat_expand {
		display: none;
	}
}

@keyframes ai_chat_spin {
	to {
		transform: rotate(360deg);
	}
}

@keyframes ai_chat_caret {
	50% {
		opacity: 0;
	}
}

@media (prefers-reduced-motion: reduce) {
	.ai_chat_pop-enter-active,
	.ai_chat_pop-leave-active,
	.ai_chat_panel,
	.ai_chat_fab,
	.ai_chat_send {
		transition: none;
	}

	.ai_msg_assistant.is-streaming::after {
		animation: none;
	}

	.ai_chat_tool-spinner {
		animation-duration: 1.6s;
	}
}
</style>
